import { useCallback, useEffect, useState } from 'react';
import { userApi } from 'lib/api/userApi';

import { UserSettings } from 'types/userSettings';
import { Family } from 'types/family';
import { CheckoutStore } from 'types/checkout';
import { useRecoilState, useRecoilValue } from 'recoil';
import { userStore } from 'stores/userStore';
import { familyStore } from 'stores/familyStore';
import { toTimezoneIndependent } from 'lib/dates';
import { auth } from 'lib/auth';
import { useLogin } from 'hooks/useLogin';

function useFamilyMembers(checkoutState?: CheckoutStore) {
	const [openFamilyDialog, setOpenFamilyDialog] = useState(false);
	const [selectedMember, setSelectedMember] = useState(
		checkoutState?.selectedFamilyMember && Array.isArray(checkoutState?.selectedFamilyMember)
			? checkoutState.selectedFamilyMember[0]
			: (checkoutState?.selectedFamilyMember as null | UserSettings) || null
	);
	const [familyAccount, setFamilyAccount] = useRecoilState<Family[] | null>(familyStore.familyAccount);

	const { isLoggedInV2 } = useLogin();

	const [familyList, setFamilyList] = useState<UserSettings[] | null>();
	const [initialFamilyId, setInitialFamilyId] = useState(false);
	const userSettings = useRecoilValue(userStore.userSettings);
	const [isFetching, setFetching] = useState(false);

	useEffect(() => {
		if (!isLoggedInV2) {
			setInitialFamilyId(false)
		}
	}, [isLoggedInV2]);

	const refreshFamilyList = useCallback((family: Family[]) => {
		// select first family if familyId > 0
		if (family?.length) {
			setFetching(true);
			userApi
				.getUserFamilyAccount(family[0].id)
				.then(familyRec => {
					familyRec.forEach(familyMember => {
						familyMember.birthDate = toTimezoneIndependent(familyMember.birthDate);
					});
					console.log('Set Data', family, familyRec)
					setFamilyAccount(family);
					setFamilyList(familyRec);
				})
				.catch(error => console.error('Failed to fetch family account', error))
				.finally(() => setFetching(false));
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const refreshSingleUserAsFamilyList = useCallback(async (userSettings?: UserSettings) => {
		try {
			// if there is no family connected to the current signed-in user
			// and the user settings are stored in the recoil store
			if (userSettings) {
				setFamilyList([{ ...userSettings, birthDate: toTimezoneIndependent(userSettings.birthDate) }]);
				return;
			}

			// fetching user data if the user settings are not stored in the recoil store.
			const userId: number = auth.getUserId();

			if (!userId) {
				return;
			}

			setFetching(true);
			const user = await userApi.getUserSettings(userId);
			setFamilyList([{ ...user, id: userId, birthDate: toTimezoneIndependent(user.birthDate) }]);
		} catch (error) {
			console.error('Failed to fetch user settings', error);
		} finally {
			setFetching(false);
		}
	}, []);

	const createFamilyAccount = async (): Promise<Family[]> => {
		try {
			const { data } = await userApi.getUserSettings_DEPRICATED();
			await userApi.postCreateFamilyAccount(data?.lastName ? `${data?.lastName}'s family` : `Family customer`);
			const newFamily = await userApi.getUserFamily();
			setFamilyAccount(newFamily);
			refreshFamilyList(newFamily);

			return newFamily;
		} catch (error) {
			console.error('Failed to create family account', error);
			return [];
		}
	};

	const handleFamilyDialogCloseEvent = (family?: Family[]) => {
		if (family || familyAccount) {
			refreshFamilyList((family ?? familyAccount) as Family[]);
		}
		setOpenFamilyDialog(false);
	};

	const handleFamilyDialogClose = (family?: Family[]) => {
		setOpenFamilyDialog(false);
		if (family || familyAccount) {
			refreshFamilyList((family ?? familyAccount) as Family[]);
		}
	};

	const handleResetFamily = useCallback(() => {
		console.log('Reset')
		setFamilyAccount(null);
		setFamilyList(null);
		setSelectedMember(null);
		setInitialFamilyId(false);
	}, [setFamilyAccount]);

	useEffect(() => {
		if (!initialFamilyId && isLoggedInV2 ) {
			(async () => {
				try {
					handleResetFamily();

					const familyList: Family[] = await userApi.getUserFamily();

					setInitialFamilyId(true);

					if (familyList.length) {
						refreshFamilyList(familyList);
						return;
					}

					refreshSingleUserAsFamilyList(userSettings!);
				} catch (error) {
					console.error(error);
				}
			})();
		}
	}, [handleResetFamily, initialFamilyId, refreshFamilyList, refreshSingleUserAsFamilyList, userSettings, isLoggedInV2]);

	return {
		openFamilyDialog,
		setOpenFamilyDialog,
		selectedMember,
		setSelectedMember,
		familyAccount,
		setFamilyAccount,
		familyList,
		setFamilyList,
		initialFamilyId,
		setInitialFamilyId,
		refreshFamilyList,
		handleFamilyDialogClose,
		handleFamilyDialogCloseEvent,
		isFetching,
		createFamilyAccount,
	};
}

export default useFamilyMembers;
